/**
 * Core analytics functions — migrated from the Solid CMS codebase.
 *
 * Key changes from Solid → Next.js:
 * - Solid signals (loginSignal, activityTimeLog) → plain module-level state helpers
 * - Solid-specific imports → @repo/shared-utils, @repo/web-store equivalents
 * - `isOpenInApp`, `getCookie`, `getPageType`, `getSessionData`, `isMobile` now come from @repo/shared-utils
 * - `logErrorToNewRelic` from @repo/shared-utils/newRelic-log-utils
 * - CleverTapConstants from local constants
 * - clevertapEvent from local clevertapevent-utils
 */

import { CleverTapConstants } from "./constants/clevertap.constants";
import {
	COMMON_SSO_ACCESS_TOKEN,
} from "@repo/shared-utils/sso.constants";
import { APP_PROMPTS } from "@repo/shared-utils/pwa.constants";
import { logErrorToNewRelic } from "@repo/shared-utils/newRelic-log-utils";
import {
	getActivityTimeLogState,
	setActivityTimeLogState,
} from "./state/activityTimeLog";
import { getLoginStatus, getIsTokenPresent } from "./state/loginState";
import { clevertapEvent } from "./utils/clevertapevent-utils";
import { isMobile } from "@repo/shared-utils/mobile-utils";
import {
	getCookie,
	getPageType,
	getSessionData,
	isBrowser,
} from "@repo/shared-utils/common-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { mapCleverTapToGA } from "./mappers";
import type {
	CleverTapPayload,
	ComponentEventInput,
	DataLayerProps,
	DataLayerState,
	DispatchContext,
	GaEventType,
	GoogleAnalyticsValue,
} from "./types";
import type { LoginStatus } from "@repo/web-store";
import type { UserSessionDataTypes } from "@repo/shared-utils/common-utils";

const LSFLAG = "_lsFlag";

const gaLoginStateText: Record<LoginStatus, string> = {
	success: "Identified",
	failure: "Non Loggedin",
	pending: "Non Loggedin",
	perpetual: "Perpetual",
};

type AnalyticsDebugGlobal = {
	console?: boolean;
	fireEvents?: boolean;
};

type AnalyticsWindow = Window & {
	dataLayer?: Record<string, GoogleAnalyticsValue>[];
	clevertap?: {
		getCleverTapID?: () => string | undefined;
		ctPageObject?: Record<string, unknown>;
	};
	__BFL_ANALYTICS_DEBUG__?: AnalyticsDebugGlobal;
	tarsier?: {
		invokePrompt?: (prompt: string) => unknown;
	};
};

export const FULLY_LOADED = "FULLY LOADED";

export const toErrorMessage = (error: unknown): string => {
	if (error instanceof Error) {
		return error.message;
	}

	try {
		return JSON.stringify(error);
	} catch {
		return String(error);
	}
};

export const composeDataLayerProps = (input: ComponentEventInput, gaEventType: GaEventType): DataLayerProps => {
	return stripUndefined({
		component: input.componentName,
		cta_text: input.ctaText,
		entry_point: input.entryPoint,
		section_title: input.sectionTitle,
		sub_section: input.subSection,
		destination_url: input.destinationUrl,
		event: gaEventType,
		event_type: input.eventType,
		click_type: input.clickType,
		element: input.element,
		tab_position: input.tabPosition === undefined ? undefined : String(input.tabPosition),
		journey_tag: input.journeyTag,
		stage_tag: input.stageTag,
		validation_error: input.validationError,
		form_name: input.formName,
		field_name: input.fieldName,
		field_value: input.fieldValue,
		submission_status: input.submissionStatus,
		time_spent: input.timeSpent,
		application_id: input.applicationId,
		product_category: input.productCategory,
		product_name: input.productName,
		page_type: input.pageType,
		page_url: input.pageUrl,
		unique_asset_id: input.uniqueAssetId,
		input_amount: input.inputAmount,
		offer_amount: input.offerAmount,
		component_id: input.componentId,
		nudge_type: input.nudgeType,
		nudge_value: input.nudgeValue,
		component_position: input.componentPosition,
		segment_name: input.segmentName,
		sprint_release: input.sprintRelease,
		loan_variant: input.loanVariant,
		loan_range: input.loanRange,
		amount_ai: input.amountAi,
		tenure: input.tenure,
		tenure_ai: input.tenureAi,
		roi: input.roi,
		roi_ai: input.roiAi,
		monthly_instalment: input.monthlyInstalment,
		native_app_version: input.nativeAppVersion,
		custom_attr1: input.customAttr1,
		custom_attr2: input.customAttr2,
		custom_attr3: input.customAttr3,
		load_type: input.loadType,
	});
};

export const buildDataLayerObject = async (baseProps: DataLayerProps): Promise<DataLayerState> => {
	const dataLayerObject: DataLayerState = { ...baseProps };

	if (isBrowser()) {
		dataLayerObject.page_url = globalThis.window.location.href;
		const analyticsWindow = globalThis.window as AnalyticsWindow;
		const nativeAppVersion = isOpenInApp()
			? (analyticsWindow.tarsier?.invokePrompt?.(APP_PROMPTS.APP_VERSION) as string)
			: "";
		dataLayerObject.native_app_version = nativeAppVersion;
	}
	const pageType = getPageType("tag-1");
	const productCategory = getPageType("tag-2");
	const productName = getPageType("tag-3");

	if (pageType) {
		dataLayerObject.page_type = pageType;
	}
	if (productCategory) {
		dataLayerObject.product_category = productCategory;
	}
	if (productName) {
		dataLayerObject.product_name = productName;
	}

	let session: UserSessionDataTypes | undefined;
	try {
		session = await getSessionData();
		if (session?.custId) {
			dataLayerObject.customer_id = session.custId;
		}
		if (session?.custType) {
			dataLayerObject.customer_tag = session.custType.toLowerCase();
		}
	} catch {
		// ignore session failures, we can proceed without them
	}

	const ctLoginStatus = resolveLoginStatus({ session, dataLayer: dataLayerObject });
	dataLayerObject.is_loggedin = resolveGaLoginState(ctLoginStatus);

	return dataLayerObject;
};

/**
 * Returns the total number of scroll actions performed by the user, throttled to avoid excessive event firing.
 * Matches the Solid CMS behaviour exactly — an IIFE that attaches a scroll listener on module load.
 */
const throttle = <Fn extends (...args: unknown[]) => unknown>(fn: Fn, delay: number) => {
	let timerId: ReturnType<typeof setTimeout> | undefined;
	return (...args: Parameters<Fn>) => {
		if (timerId) return;
		timerId = setTimeout(() => {
			fn(...args);
			timerId = undefined;
		}, delay);
	};
};

const getScrollCount = (() => {
	let scrollCount = 0;
	if (isBrowser()) {
		const scrollHandler = throttle(() => { scrollCount += 1; }, 500);
		globalThis.window.addEventListener("scroll", scrollHandler);
	}
	return () => scrollCount;
})();

export const composeCleverTapPayload = (
	input: ComponentEventInput,
	dataLayerObject: DataLayerState,
): CleverTapPayload => {
	const campaignCookie = safeParseCookie<Record<string, string>>("campaignCookie");
	const lastClickCookie = safeParseCookie<Record<string, string>>("LastClickCookie");
	const gaId = getCookie("_ga") ?? undefined;
	const pageName = computePageName(dataLayerObject);
	const epSource = resolveEpSource(lastClickCookie?.click);
	const pageObject = getClevertapPageObject();
	const loginStatusForCt = resolveLoginStatus({ dataLayer: dataLayerObject });
	let sanitizedOverrides = input.cleverTapOverrides ? sanitizeToStrings(input.cleverTapOverrides) : undefined;
	const bannerFlag = sanitizedOverrides?.isBannerComponent;
	const bannerFieldsEnabled = shouldAttachBannerFields(bannerFlag);
	if (sanitizedOverrides) {
		const { isBannerComponent: _bannerFlag, ...rest } = sanitizedOverrides;
		sanitizedOverrides = rest;
	}

	const hostname = isBrowser() ? globalThis.window.location.hostname : undefined;
	const language =
		typeof document !== "undefined" && typeof document.documentElement?.lang === "string"
			? document.documentElement.lang
			: undefined;
	const basePayload = sanitizeToStrings({
		EVENT_NAME: input.eventName,
		EP_COMPONENT_NAME: dataLayerObject.component,
		EP_SECTION: dataLayerObject.section_title,
		EP_SUB_SECTION: dataLayerObject.sub_section,
		EP_CTA_TEXT: dataLayerObject.cta_text,
		EP_DESTINATION: dataLayerObject.destination_url,
		EP_POSITION: input.position ?? dataLayerObject.tab_position,
		EP_CLICK_TYPE: dataLayerObject.click_type,
		EP_EVENT_TYPE: dataLayerObject.event_type,
		EP_ID: dataLayerObject.unique_asset_id,
		EP_ENTRYPOINT: dataLayerObject.entry_point,
		EP_OFFER_AMOUNT: dataLayerObject.offer_amount,
		EP_REFERENCE_ID: dataLayerObject.component_id,
		EP_NUDGE: dataLayerObject.nudge_type,
		EP_NUDGE_VALUE: dataLayerObject.nudge_value,
		EP_SR_NUMBER: dataLayerObject.component_position,
		EP_SEGMENT: dataLayerObject.segment_name,
		EP_SPRINT: dataLayerObject.sprint_release,
		EP_LOAN_VARIANT: dataLayerObject.loan_variant,
		EP_LOAN_RANGE: dataLayerObject.loan_range,
		EP_AMOUNT_AI: dataLayerObject.amount_ai,
		EP_TENURE: dataLayerObject.tenure,
		EP_TENURE_AI: dataLayerObject.tenure_ai,
		EP_ROI: dataLayerObject.roi,
		EP_ROI_AI: dataLayerObject.roi_ai,
		EP_EMI: dataLayerObject.monthly_instalment,
		EP_AMOUNT_ENTERED: dataLayerObject.input_amount,
		EP_NATIVE_VERSION: dataLayerObject.native_app_version,
		EP_SUCCESS_EVENT_TYPE: dataLayerObject.submission_status,
		EP_PAGE_NAME: pageName,
		EP_PAGE_URL: dataLayerObject.page_url,
		EP_PROPERTY_TYPE: dataLayerObject.page_type,
		EP_HOSTNAME: hostname,
		EP_LANG: language,
		EP_SOURCE: epSource,
		EP_USER_TYPE: dataLayerObject.customer_tag ? dataLayerObject.customer_tag.toUpperCase() : undefined,
		EP_CUSTOMER_TYPE: dataLayerObject.customer_tag ? dataLayerObject.customer_tag.toUpperCase() : undefined,
		EP_JOURNEY: dataLayerObject.journey_tag,
		EP_STAGE: dataLayerObject.stage_tag,
		EP_BUSINESS: dataLayerObject.product_name,
		EP_PRODUCT_NAME: dataLayerObject.product_name,
		EP_PRODUCT_CATEGORY: dataLayerObject.product_category,
		EP_PLATFORM: isOpenInApp() ? CleverTapConstants.APP : CleverTapConstants.WEB,
		DEVICE_TYPE: resolveDeviceType(),
		EP_LOGIN_STATUS: loginStatusForCt,
		EP_GA_ID: gaId,
		EP_LAST_CLICK: lastClickCookie?.click,
		EP_UTM_SOURCE: campaignCookie?.utm_source,
		EP_UTM_MEDIUM: campaignCookie?.utm_medium,
		EP_UTM_CAMPAIGN: campaignCookie?.utm_campaign,
		EP_UTM_CONTENT: campaignCookie?.utm_content,
		EP_CT_ID: getCleverTapId(),
		EP_TIMESTAMP: new Date().toLocaleString(),
		EP_LOAD_TYPE: dataLayerObject.load_type,
		EP_LOAD_TIME: getPageLoadTime(dataLayerObject),
		EP_PAGE_LOAD: dataLayerObject.page_load_type,
		EP_ACTIVITY_TIMELOG: getActivityTimeLog(),
		EP_SCROLL: String(getScrollCount()),
		EP_VALIDATION_ERROR: dataLayerObject.validation_error,
		EP_APPLICATION_ID: dataLayerObject.application_id,
		EP_CUST_ATTRIBUTE_1: dataLayerObject.custom_attr1,
		EP_CUST_ATTRIBUTE_2: dataLayerObject.custom_attr2,
		EP_CUST_ATTRIBUTE_3: dataLayerObject.custom_attr3,
	});

	return {
		...basePayload,
		...(bannerFieldsEnabled ? buildBannerFields(dataLayerObject) : {}),
		...pageObject,
		...(sanitizedOverrides ?? ({} as CleverTapPayload)),
	};
};

export const deriveGaEventType = (explicit?: GaEventType, eventType?: string): GaEventType => {
	if (explicit) {
		return explicit;
	}

	if (eventType) {
		const normalized = eventType.toLowerCase();
		const allowed: GaEventType[] = ["view", "click", "scroll", "impression", "load", "prefilled"];
		if ((allowed as string[]).includes(normalized)) {
			return normalized as GaEventType;
		}

		if (normalized.includes("view")) {
			return "view";
		}
		if (normalized.includes("load")) {
			return "load";
		}
		if (normalized.includes("scroll")) {
			return "scroll";
		}
		if (normalized.includes("impress")) {
			return "impression";
		}
		if (normalized.includes("prefill")) {
			return "prefilled";
		}
	}

	return "click";
};

export const dispatchAnalyticsEvent = async (
	eventName: string,
	ctPayload: CleverTapPayload,
	dataLayerObject: DataLayerState,
	context: DispatchContext,
): Promise<void> => {
	if (!isBrowser()) return;

	const analyticsWindow = globalThis.window as AnalyticsWindow;
	const debugBehavior = analyticsWindow.__BFL_ANALYTICS_DEBUG__;

	if (eventName) {
		updateActivityTimeLog();
		try {
			if (debugBehavior?.console) {
				console.log("[CT]", eventName, ctPayload);
			}

			if (debugBehavior?.fireEvents !== false) {
				clevertapEvent(eventName, ctPayload);
			}
		} catch (error) {
			const errorMessage = toErrorMessage(error);
			logErrorToNewRelic("Failed to send CleverTap event", {
				eventName,
				componentName: context.componentName,
				error: errorMessage,
			});
			console.error(`Failed to send CT event ${eventName}: ${errorMessage}`);
		}
	}

	try {
		const gaPayload = mapCleverTapToGA({
			ctPayload,
			dataLayer: dataLayerObject,
			destinationUrl: context.destinationUrl,
			position: context.position,
			gaEventType: context.gaEventType,
			overrides: context.gaOverrides,
		});
		if (debugBehavior?.console) {
			console.log("[GA]", gaPayload.event, gaPayload);
		}

		if (debugBehavior?.fireEvents === false) {
			return;
		}
		analyticsWindow.dataLayer = analyticsWindow.dataLayer || [];
		analyticsWindow.dataLayer.push(gaPayload);
	} catch (error) {
		const errorMessage = toErrorMessage(error);
		logErrorToNewRelic("Failed to send GA event", {
			gaEventType: context.gaEventType,
			componentName: context.componentName ?? "unknown",
			error: errorMessage,
		});
		console.error(`Failed to send GA event ${eventName}: ${errorMessage}`);
	}
};

export const markViewEventTriggered = (): void => {
	try {
		setActivityTimeLogState((prev) => ({
			...prev,
			ctViewdEventTime: Date.now(),
			viewEventTriggered: true,
		}));
	} catch {
		// ignore state errors
	}
};

const buildBannerFields = (dataLayerObject: DataLayerState): CleverTapPayload => {
	const toOptionalString = (value?: string | number | boolean): string | undefined => {
		if (value === undefined || value === null || value === "") {
			return undefined;
		}
		return String(value);
	};

	let moduleName: string | undefined;
	if (dataLayerObject.page_type && dataLayerObject.product_category) {
		moduleName = `${dataLayerObject.page_type}_${dataLayerObject.product_category}`;
	}

	let moduleSection: string | undefined;
	if (dataLayerObject.page_type === "homepage") {
		moduleSection = dataLayerObject.section_title;
	} else if (dataLayerObject.page_type && dataLayerObject.component) {
		moduleSection = `${dataLayerObject.page_type}_${dataLayerObject.component}`;
	}

	return sanitizeToStrings({
		EP_BANNER_CTA: toOptionalString(dataLayerObject.cta_text),
		EP_BANNER_PRODUCT: toOptionalString(dataLayerObject.product_name),
		EP_BANNER_SEQUENCE: toOptionalString(dataLayerObject.tab_position),
		EP_BANNER_TEXT: toOptionalString(dataLayerObject.section_title),
		EP_BANNER_MODULE_NAME: moduleName,
		EP_BANNER_SUBDIVISION: toOptionalString(dataLayerObject.section_title),
		EP_BANNER_TYPE: toOptionalString(dataLayerObject.component),
		EP_BANNER_CLICK_TYPE: toOptionalString(dataLayerObject.cta_text),
		EP_BANNER_DESCRIPTION: toOptionalString(dataLayerObject.section_title),
		EP_BANNER_MODULE_SECTION: moduleSection,
	});
};

const shouldAttachBannerFields = (flag?: string): boolean => {
	if (!flag) {
		return false;
	}

	return flag.trim().toLowerCase() === "true";
};

const updateActivityTimeLog = (): void => {
	try {
		const state = getActivityTimeLogState();
		if (state.viewEventTriggered) {
			setActivityTimeLogState((prev) => ({
				...prev,
				ctClickEventTime: Date.now(),
				viewEventTriggered: false,
			}));
		}
	} catch {
		// ignore state errors
	}
};

const stripUndefined = <T extends Record<string, unknown>>(record: T): T => {
	const clean: Record<string, unknown> = {};

	for (const [key, value] of Object.entries(record)) {
		if (value !== undefined && value !== null) {
			clean[key] = value;
		}
	}

	return clean as T;
};

const sanitizeToStrings = (record: Record<string, unknown>): CleverTapPayload => {
	const clean: CleverTapPayload = {};

	for (const [key, value] of Object.entries(record)) {
		if (value !== undefined && value !== null && value !== "") {
			if (typeof value === "string") {
				clean[key] = value;
			} else if (typeof value === "object") {
				clean[key] = JSON.stringify(value);
			} else if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
				clean[key] = String(value);
			}
		}
	}

	return clean;
};

const resolveDeviceType = (): string => {
	if (isOpenInApp()) {
		return CleverTapConstants.DEVICEAPP;
	}

	return isMobile ? CleverTapConstants.MOBWEB : CleverTapConstants.DESKWEB;
};

const resolveLoginStatus = (context?: {
	session?: UserSessionDataTypes;
	dataLayer?: DataLayerState;
}): "logged-in" | "non-logged-in" => {
	if (isSignalLoggedInState()) {
		return "logged-in";
	}

	if (hasDataLayerIdentity(context?.dataLayer)) {
		return "logged-in";
	}

	if (hasSessionIdentity(context?.session)) {
		return "logged-in";
	}

	const ctHint = readCleverTapLoginHint();
	if (ctHint) {
		return ctHint;
	}

	if (hasCookieLoginFlag() || hasStoredAccessToken()) {
		return "logged-in";
	}

	if (isOpenInApp() && getIsTokenPresent()) {
		return "logged-in";
	}

	return "non-logged-in";
};

const resolveGaLoginState = (ctLoginStatus: "logged-in" | "non-logged-in"): string => {
	const loginState = getLoginStatus();
	if (loginState === "perpetual") {
		return gaLoginStateText.perpetual;
	}

	return ctLoginStatus === "non-logged-in" ? gaLoginStateText.failure : gaLoginStateText.success;
};

const isSignalLoggedInState = (): boolean => {
	const loginState = getLoginStatus();
	return loginState === "success" || loginState === "perpetual";
};

const hasSessionIdentity = (session?: UserSessionDataTypes): boolean => {
	if (!session) {
		return false;
	}

	const normalizedType = session.custType?.trim().toUpperCase();
	const hasCustId =
		typeof session.custId === "string" ? session.custId.trim() !== "" : typeof session.custId === "number";
	const hasMobile = typeof session.mobileNo === "string" && session.mobileNo.trim() !== "";
	return Boolean(hasCustId || hasMobile || (normalizedType && normalizedType !== "NTB"));
};

const hasDataLayerIdentity = (dataLayer?: DataLayerState): boolean => {
	if (!dataLayer) {
		return false;
	}

	const normalizedType = dataLayer.customer_tag?.trim().toUpperCase();
	const hasCustomerId =
		typeof dataLayer.customer_id === "string"
			? dataLayer.customer_id.trim() !== ""
			: typeof dataLayer.customer_id === "number";
	return Boolean(hasCustomerId || (normalizedType && normalizedType !== "NTB"));
};

const readCleverTapLoginHint = (): "logged-in" | "non-logged-in" | undefined => {
	if (!isBrowser()) {
		return undefined;
	}

	const analyticsWindow = globalThis.window as AnalyticsWindow;
	const hint = analyticsWindow.clevertap?.ctPageObject?.EP_LOGIN_STATUS;
	if (typeof hint === "string") {
		const normalized = hint.trim().toLowerCase();
		if (normalized === "logged-in" || normalized === "non-logged-in") {
			return normalized;
		}
	}

	return undefined;
};

const hasCookieLoginFlag = (): boolean => {
	const flag = getCookie(LSFLAG);
	return typeof flag === "string" && flag.toLowerCase() === "true";
};

const hasStoredAccessToken = (): boolean => {
	if (!isBrowser()) {
		return false;
	}

	try {
		const storedToken = globalThis.window.localStorage?.getItem(COMMON_SSO_ACCESS_TOKEN);
		return Boolean(storedToken && storedToken !== "dummy");
	} catch {
		return false;
	}
};

const getCleverTapId = (): string | undefined => {
	if (!isBrowser()) {
		return undefined;
	}

	const analyticsWindow = globalThis.window as AnalyticsWindow;
	return analyticsWindow.clevertap?.getCleverTapID?.() ?? undefined;
};

const getClevertapPageObject = (): CleverTapPayload => {
	if (!isBrowser()) {
		return {};
	}

	const analyticsWindow = globalThis.window as AnalyticsWindow;
	const pageObject = analyticsWindow.clevertap?.ctPageObject ?? {};
	return sanitizeToStrings(pageObject);
};

const safeParseCookie = <T>(name: string): T | undefined => {
	const raw = getCookie(name);
	if (!raw) {
		return undefined;
	}

	try {
		return JSON.parse(raw) as T;
	} catch {
		return undefined;
	}
};

const computePageName = (dataLayer: DataLayerState): string | undefined => {
	if (dataLayer.product_name) {
		const suffix = dataLayer.page_type ? ` ${dataLayer.page_type}` : "";
		return `${dataLayer.product_name}${suffix}`.replaceAll("-", " ").toUpperCase();
	}

	if (dataLayer.product_category) {
		return dataLayer.product_category.replaceAll("-", " ").toUpperCase();
	}

	return undefined;
};

const resolveEpSource = (lastClick?: string): string | undefined => {
	if (isOpenInApp()) {
		return "";
	}

	return lastClick;
};

const getPageLoadTime = (dataLayerObject: DataLayerProps): string | undefined => {
	if (isBrowser()) {
		const cachedSeconds = (
			globalThis.window as unknown as {
				__BFL_E2E_PAGE_LOAD_TIME_SECONDS__?: number;
			}
		).__BFL_E2E_PAGE_LOAD_TIME_SECONDS__;
		if (typeof cachedSeconds === "number" && Number.isFinite(cachedSeconds) && cachedSeconds >= 0) {
			return `${cachedSeconds.toFixed(2)} SECONDS`;
		}
	}

	return dataLayerObject.page_load_time;
};

const getActivityTimeLog = (): string | undefined => {
	const { ctClickEventTime, ctViewdEventTime } = getActivityTimeLogState();
	const activityTime = ctClickEventTime - ctViewdEventTime;
	if (activityTime > 0) {
		return `${(activityTime / 1000).toFixed(2)} SECONDS`;
	}
	return undefined;
};
